import { useState, useRef, useCallback, type KeyboardEvent } from "react";
import { useOrgUsers } from "@/hooks/use-org-users";
import { MentionDropdown, filterMentionUsers } from "./MentionDropdown";
import { FieldTagDropdown, filterFieldTags, useFieldTags, type FieldTagItem } from "./FieldTagDropdown";

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (mentionedUids: string[], referencedFields?: Array<{fieldId: string; crfId: string; domain: string; variable: string}>) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
}

export function MentionInput({
  value,
  onChange,
  onSubmit,
  placeholder = "Add a comment...",
  disabled,
  className,
  autoFocus,
}: MentionInputProps) {
  const { data: users = [] } = useOrgUsers();
  const allFields = useFieldTags();
  const inputRef = useRef<HTMLInputElement>(null);

  // @-mention state
  const [mentionActive, setMentionActive] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionStart, setMentionStart] = useState(0);
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [mentionedUids, setMentionedUids] = useState<string[]>([]);

  // #field-tag state
  const [fieldTagActive, setFieldTagActive] = useState(false);
  const [fieldTagFilter, setFieldTagFilter] = useState("");
  const [fieldTagStart, setFieldTagStart] = useState(0);
  const [fieldTagSelectedIndex, setFieldTagSelectedIndex] = useState(0);
  const [referencedFields, setReferencedFields] = useState<Array<{fieldId: string; crfId: string; domain: string; variable: string}>>([]);

  const dropdownPosition = { top: -4, left: 0 };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onChange(newValue);

      const cursor = e.target.selectionStart ?? newValue.length;
      const textBeforeCursor = newValue.slice(0, cursor);

      // Check for # field tag trigger
      const hashIndex = textBeforeCursor.lastIndexOf("#");
      if (
        hashIndex >= 0 &&
        (hashIndex === 0 || textBeforeCursor[hashIndex - 1] === " ")
      ) {
        const filter = textBeforeCursor.slice(hashIndex + 1);
        if (!filter.includes(" ")) {
          setFieldTagActive(true);
          setFieldTagFilter(filter);
          setFieldTagStart(hashIndex);
          setFieldTagSelectedIndex(0);
          setMentionActive(false);
          return;
        }
      }
      setFieldTagActive(false);

      // Check for @ mention trigger
      const atIndex = textBeforeCursor.lastIndexOf("@");
      if (
        atIndex >= 0 &&
        (atIndex === 0 || textBeforeCursor[atIndex - 1] === " ")
      ) {
        const filter = textBeforeCursor.slice(atIndex + 1);
        if (!filter.includes(" ")) {
          setMentionActive(true);
          setMentionFilter(filter);
          setMentionStart(atIndex);
          setMentionSelectedIndex(0);
          return;
        }
      }

      setMentionActive(false);
    },
    [onChange],
  );

  const handleSelectUser = useCallback(
    (user: { uid: string; displayName: string }) => {
      const before = value.slice(0, mentionStart);
      const after = value.slice(
        mentionStart + 1 + mentionFilter.length,
      );
      const newValue = `${before}@${user.displayName} ${after}`;
      onChange(newValue);
      setMentionActive(false);
      setMentionFilter("");
      setMentionedUids((prev) =>
        prev.includes(user.uid) ? prev : [...prev, user.uid],
      );

      setTimeout(() => {
        if (inputRef.current) {
          const pos = mentionStart + user.displayName.length + 2;
          inputRef.current.focus();
          inputRef.current.setSelectionRange(pos, pos);
        }
      }, 0);
    },
    [value, mentionStart, mentionFilter, onChange],
  );

  const handleSelectField = useCallback(
    (field: FieldTagItem) => {
      const before = value.slice(0, fieldTagStart);
      const after = value.slice(
        fieldTagStart + 1 + fieldTagFilter.length,
      );
      const newValue = `${before}#${field.tag} ${after}`;
      onChange(newValue);
      setFieldTagActive(false);
      setFieldTagFilter("");
      setReferencedFields((prev) => {
        const exists = prev.some((f) => f.fieldId === field.fieldId && f.crfId === field.crfId);
        if (exists) return prev;
        return [...prev, { fieldId: field.fieldId, crfId: field.crfId, domain: field.domain, variable: field.variable }];
      });

      setTimeout(() => {
        if (inputRef.current) {
          const pos = fieldTagStart + field.tag.length + 2;
          inputRef.current.focus();
          inputRef.current.setSelectionRange(pos, pos);
        }
      }, 0);
    },
    [value, fieldTagStart, fieldTagFilter, onChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      // Handle field tag keyboard nav
      if (fieldTagActive) {
        const filtered = filterFieldTags(allFields, fieldTagFilter);

        if (e.key === "ArrowDown") {
          e.preventDefault();
          setFieldTagSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setFieldTagSelectedIndex((i) => Math.max(i - 1, 0));
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          if (filtered[fieldTagSelectedIndex]) {
            handleSelectField(filtered[fieldTagSelectedIndex]);
          }
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setFieldTagActive(false);
          return;
        }
      }

      // Handle mention keyboard nav
      if (mentionActive) {
        const filtered = filterMentionUsers(users, mentionFilter);

        if (e.key === "ArrowDown") {
          e.preventDefault();
          setMentionSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setMentionSelectedIndex((i) => Math.max(i - 1, 0));
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          if (filtered[mentionSelectedIndex]) {
            handleSelectUser(filtered[mentionSelectedIndex]);
          }
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setMentionActive(false);
          return;
        }
      }

      // Normal Enter = submit
      if (e.key === "Enter" && !e.shiftKey && !mentionActive && !fieldTagActive) {
        e.preventDefault();
        if (value.trim()) {
          onSubmit(mentionedUids, referencedFields.length > 0 ? referencedFields : undefined);
          setMentionedUids([]);
          setReferencedFields([]);
        }
      }
    },
    [fieldTagActive, allFields, fieldTagFilter, fieldTagSelectedIndex, handleSelectField, mentionActive, users, mentionFilter, mentionSelectedIndex, handleSelectUser, value, onSubmit, mentionedUids, referencedFields],
  );

  return (
    <div className="relative flex-1">
      {mentionActive && (
        <div className="absolute bottom-full left-0 mb-1">
          <MentionDropdown
            users={users}
            filter={mentionFilter}
            selectedIndex={mentionSelectedIndex}
            onSelect={handleSelectUser}
            position={dropdownPosition}
          />
        </div>
      )}
      {fieldTagActive && (
        <div className="absolute bottom-full left-0 mb-1">
          <FieldTagDropdown
            filter={fieldTagFilter}
            selectedIndex={fieldTagSelectedIndex}
            onSelect={handleSelectField}
            position={dropdownPosition}
          />
        </div>
      )}
      <input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className={
          className ??
          "w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        }
      />
    </div>
  );
}
